import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { GraduationCap, Search, Clock, Globe as GlobeIcon, Sparkles, Loader2 } from 'lucide-react';
import { CourseVisual } from '../../components/practical-courses/CourseVisual';

const backend = process.env.REACT_APP_BACKEND_URL;

export default function PracticalCoursesPage() {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [country, setCountry] = useState('all');
    const [category, setCategory] = useState('all');
    const [categories, setCategories] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search.trim()) params.append('search', search.trim());
            if (country && country !== 'all') params.append('country', country);
            if (category && category !== 'all') params.append('category', category);
            params.append('limit', '48');
            const res = await fetch(`${backend}/api/practical-courses?${params}`);
            const data = await res.json();
            setCourses(data.courses || []);
            // Extract unique categories
            const cats = Array.from(new Set((data.courses || []).map((c) => c.category).filter(Boolean)));
            if (cats.length && categories.length === 0) setCategories(cats);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country, category]);

    return (
        <Layout>
            {/* Hero */}
            <div className="relative overflow-hidden bg-gradient-to-br from-primary-900 via-primary-950 to-violet-950 text-white">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <div className="flex items-center gap-2 text-sm text-primary-200 mb-3">
                        <Sparkles className="w-4 h-4" /> Career-boosting training
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
                        Practical Courses
                    </h1>
                    <p className="text-lg text-primary-100 max-w-2xl">
                        Hand-picked, employer-focused programmes from vetted training providers. Move faster into data, privacy, tech, and compliance roles.
                    </p>

                    {/* Search / filters bar */}
                    <div className="mt-8 max-w-4xl">
                        <div className="backdrop-blur-xl bg-white/10 rounded-2xl p-2 border border-white/10 shadow-2xl shadow-primary-900/50">
                            <div className="flex flex-col md:flex-row gap-2">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-300" />
                                    <Input
                                        placeholder="Search courses, providers, keywords..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && load()}
                                        className="pl-12 h-14 bg-white/5 border-white/10 text-white placeholder:text-primary-300/60 rounded-xl"
                                        data-testid="courses-search-input"
                                    />
                                </div>
                                <Select value={country} onValueChange={setCountry}>
                                    <SelectTrigger className="w-full md:w-44 h-14 bg-white/5 border-white/10 text-white rounded-xl" data-testid="courses-country-select">
                                        <SelectValue placeholder="Country" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">🌍 All Countries</SelectItem>
                                        <SelectItem value="UK">🇬🇧 UK</SelectItem>
                                        <SelectItem value="Canada">🇨🇦 Canada</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={category} onValueChange={setCategory}>
                                    <SelectTrigger className="w-full md:w-48 h-14 bg-white/5 border-white/10 text-white rounded-xl" data-testid="courses-category-select">
                                        <SelectValue placeholder="Category" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Categories</SelectItem>
                                        {categories.map((c) => (
                                            <SelectItem key={c} value={c}>{c}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button onClick={load} className="h-14 px-8 bg-gradient-to-r from-primary-500 to-violet-500 hover:from-primary-400 hover:to-violet-400 text-white font-semibold rounded-xl" data-testid="courses-search-btn">
                                    <Search className="w-5 h-5 mr-2" /> Search
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Grid */}
            <div className="bg-neutral-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {loading ? (
                        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>
                    ) : courses.length === 0 ? (
                        <div className="text-center py-20 text-neutral-600" data-testid="no-courses-msg">
                            <GraduationCap className="w-16 h-16 mx-auto text-neutral-300 mb-3" />
                            <p className="text-lg">No courses match your search yet.</p>
                            <p className="text-sm text-neutral-500 mt-1">Try clearing filters or check back soon.</p>
                        </div>
                    ) : (
                        <>
                            <div className="mb-6 text-sm text-neutral-600">{courses.length} course{courses.length !== 1 ? 's' : ''} available</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {courses.map((c) => (
                                    <CourseCard key={c.course_id} course={c} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
}

const CourseCard = ({ course }) => (
    <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden bg-white border-neutral-200" data-testid={`course-card-${course.course_id}`}>
        <div className="relative h-40 overflow-hidden">
            {course.provider_logo_url ? (
                <div className="h-full bg-gradient-to-br from-primary-100 via-primary-50 to-violet-100 flex items-center justify-center">
                    <img
                        src={course.provider_logo_url.startsWith('/') ? `${backend}${course.provider_logo_url}` : course.provider_logo_url}
                        alt={course.provider_name}
                        className="max-h-24 max-w-[70%] object-contain"
                    />
                </div>
            ) : (
                <CourseVisual category={course.category} size="card" />
            )}
            {course.is_featured && (
                <span className="absolute top-3 right-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-md">
                    ⭐ Featured
                </span>
            )}
            {course.country && (
                <span className="absolute top-3 left-3 bg-white/90 backdrop-blur text-xs px-2 py-0.5 rounded-full text-neutral-700 font-medium">
                    {course.country === 'Canada' ? '🇨🇦' : '🇬🇧'} {course.country}
                </span>
            )}
        </div>
        <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2 text-xs text-primary-700 font-semibold uppercase tracking-wide">
                {course.category || 'Course'}
            </div>
            <h3 className="text-lg font-bold text-neutral-900 mb-1 line-clamp-2 group-hover:text-primary-700 transition-colors" data-testid="course-title">
                {course.title}
            </h3>
            <div className="text-sm text-neutral-500 mb-3">{course.provider_name}</div>
            <p className="text-sm text-neutral-700 line-clamp-3 mb-4">{course.short_description}</p>

            <div className="flex flex-wrap gap-2 mb-4 text-xs">
                {course.duration && (
                    <span className="inline-flex items-center gap-1 bg-neutral-100 text-neutral-700 px-2 py-1 rounded"><Clock className="w-3 h-3" /> {course.duration}</span>
                )}
                {course.format && (
                    <span className="inline-flex items-center gap-1 bg-neutral-100 text-neutral-700 px-2 py-1 rounded"><GlobeIcon className="w-3 h-3" /> {course.format}</span>
                )}
                {course.price && (
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-1 rounded font-medium">{course.price}</span>
                )}
            </div>

            {course.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                    {course.tags.slice(0, 4).map((t) => (
                        <Badge key={t} variant="secondary" className="text-xs bg-primary-50 text-primary-700">{t}</Badge>
                    ))}
                </div>
            )}

            <div className="flex gap-2 pt-2 border-t border-neutral-100">
                <Link to={`/practical-courses/${course.slug}`} className="flex-1">
                    <Button className="w-full bg-gradient-to-r from-primary-500 to-violet-500 hover:from-primary-400 hover:to-violet-400 text-white" data-testid={`course-view-${course.slug}`}>
                        View Details & Enquire
                    </Button>
                </Link>
            </div>
        </CardContent>
    </Card>
);
